import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Mail, Phone, StickyNote, Trash2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { CardSkeleton, FeedbackMessage } from "@/components/ui/feedback";
import { Label, Textarea } from "@/components/ui/input";
import { DemoBanner } from "@/components/portal/DemoBanner";
import {
  addContactMessageNote,
  deleteContactMessageNote,
  listContactMessages,
  updateContactMessage,
  type ContactMessage,
  type ContactMessageNote,
  type ContactMessageStatus,
} from "@/features/contact/repo";

const statusVariant: Record<ContactMessageStatus, "default" | "warning" | "success"> = {
  new: "warning",
  in_review: "default",
  closed: "success",
};

const statusLabel: Record<ContactMessageStatus, string> = {
  new: "New",
  in_review: "In review",
  closed: "Closed",
};

const fmt = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export default function InternalMessages() {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [selected, setSelected] = useState<ContactMessage | null>(null);
  const [filter, setFilter] = useState<"all" | ContactMessageStatus>("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteNote, setPendingDeleteNote] = useState<{
    message: ContactMessage;
    note: ContactMessageNote;
  } | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const data = await listContactMessages();
      setMessages(data);
      setSelected((current) =>
        current ? data.find((m) => m.id === current.id) ?? data[0] ?? null : data[0] ?? null,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load messages.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const filtered = useMemo(
    () => (filter === "all" ? messages : messages.filter((m) => m.status === filter)),
    [messages, filter],
  );

  const counts = useMemo(
    () => ({
      new: messages.filter((m) => m.status === "new").length,
      inReview: messages.filter((m) => m.status === "in_review").length,
      closed: messages.filter((m) => m.status === "closed").length,
    }),
    [messages],
  );

  async function setStatus(id: string, status: ContactMessageStatus) {
    setSaving(true);
    setError(null);
    try {
      await updateContactMessage(id, { status });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update message.");
    } finally {
      setSaving(false);
    }
  }

  async function saveNotes(message: ContactMessage, note: string) {
    setSaving(true);
    setError(null);
    try {
      await addContactMessageNote(message.id, note);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save notes.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteNote(message: ContactMessage, note: ContactMessageNote) {
    setPendingDeleteNote({ message, note });
  }

  async function confirmDeleteNote() {
    if (!pendingDeleteNote) return;
    setSaving(true);
    setError(null);
    try {
      await deleteContactMessageNote(pendingDeleteNote.message.id, pendingDeleteNote.note.id);
      setPendingDeleteNote(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete note.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Seo title="Messages" noindex />
      <DemoBanner />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Contact messages</h1>
          <p className="mt-1 max-w-2xl text-sm text-brand-500">
            Review quote requests, contact form messages, and dispatch questions
            submitted from the public Contact page.
          </p>
        </div>
        <div className="grid w-full grid-cols-3 gap-2 text-center text-xs sm:w-auto">
          <Stat label="New" value={counts.new} />
          <Stat label="In review" value={counts.inReview} />
          <Stat label="Closed" value={counts.closed} />
        </div>
      </div>

      {error && (
        <FeedbackMessage tone="error" className="mt-4">
          {error}
        </FeedbackMessage>
      )}

      <div className="mt-6 w-full max-w-xs">
        <Label htmlFor="message-filter">Filter messages</Label>
        <select
          id="message-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value as "all" | ContactMessageStatus)}
          className="mt-1 flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
        >
          <option value="all">All messages</option>
          <option value="new">New</option>
          <option value="in_review">In review</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {loading ? (
        <div className="mt-6 grid gap-3 lg:grid-cols-[minmax(0,1fr)_420px]">
          <div className="grid gap-3">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </div>
          <CardSkeleton rows={5} />
        </div>
      ) : (
        <div className="mt-6 grid min-w-0 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
          <div className="grid content-start gap-3">
            {filtered.length === 0 && (
              <EmptyState
                title="No messages here"
                body="Try a different status filter, or check back after someone submits the public Contact form."
              />
            )}
            {filtered.map((message) => {
              const active = selected?.id === message.id;
              return (
                <button
                  key={message.id}
                  type="button"
                  onClick={() => setSelected(message)}
                  className={`rounded-xl border bg-white p-4 text-left shadow-sm transition ${
                    active
                      ? "border-accent-500 ring-2 ring-accent-500/10"
                      : "border-brand-100 hover:border-brand-200 hover:shadow-md"
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="break-words font-medium text-brand-900">{message.name}</p>
                      <p className="text-xs text-brand-400">Submitted {fmt(message.createdAt)}</p>
                    </div>
                    <Badge variant={statusVariant[message.status]}>
                      {statusLabel[message.status]}
                    </Badge>
                  </div>
                  <p className="mt-3 line-clamp-2 text-sm text-brand-600">
                    {message.message}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-brand-500">
                    <span className="break-all">{message.email}</span>
                    {message.phone && <span>{message.phone}</span>}
                  </div>
                </button>
              );
            })}
          </div>

          <aside className="min-w-0 rounded-xl border border-brand-100 bg-white p-5 shadow-sm lg:sticky lg:top-6 lg:self-start">
            {selected ? (
              <MessageDetail
                message={selected}
                saving={saving}
                onStatus={setStatus}
                onSaveNotes={saveNotes}
                onDeleteNote={deleteNote}
              />
            ) : (
              <p className="text-sm text-brand-500">Select a message to review details.</p>
            )}
          </aside>
        </div>
      )}
      <ConfirmDialog
        open={!!pendingDeleteNote}
        title="Delete your note?"
        body="This removes only the note you wrote. Other message history stays saved."
        confirmLabel="Delete note"
        tone="danger"
        busy={saving}
        onConfirm={() => void confirmDeleteNote()}
        onCancel={() => setPendingDeleteNote(null)}
      />
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-brand-100 px-3 py-2">
      <div className="text-lg font-bold text-brand-900">{value}</div>
      {label}
    </div>
  );
}

function MessageDetail({
  message,
  saving,
  onStatus,
  onSaveNotes,
  onDeleteNote,
}: {
  message: ContactMessage;
  saving: boolean;
  onStatus: (id: string, status: ContactMessageStatus) => void;
  onSaveNotes: (message: ContactMessage, note: string) => Promise<void>;
  onDeleteNote: (message: ContactMessage, note: ContactMessageNote) => Promise<void>;
}) {
  const [noteDraft, setNoteDraft] = useState("");

  useEffect(() => {
    setNoteDraft("");
  }, [message.id]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="break-words text-lg font-bold text-brand-900">{message.name}</h2>
          <p className="text-sm text-brand-500">Contact / quote request</p>
        </div>
        <Badge variant={statusVariant[message.status]}>
          {statusLabel[message.status]}
        </Badge>
      </div>

      <div className="space-y-2 text-sm text-brand-700">
        <a href={`mailto:${message.email}`} className="flex min-w-0 items-center gap-2 break-all hover:underline">
          <Mail className="h-4 w-4 text-accent-500" />
          {message.email}
        </a>
        {message.phone && (
          <a href={`tel:${message.phone.replace(/[^+\d]/g, "")}`} className="flex min-w-0 items-center gap-2 break-all hover:underline">
            <Phone className="h-4 w-4 text-accent-500" />
            {message.phone}
          </a>
        )}
      </div>

      <div className="mt-5 rounded-lg bg-brand-50 p-4 text-sm text-brand-700">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-400">
          Message
        </div>
        <p className="whitespace-pre-wrap">{message.message}</p>
      </div>

      <div className="mt-5">
        <Label htmlFor={`notes-${message.id}`} className="flex items-center gap-1">
          <StickyNote className="h-4 w-4" />
          Add internal note
        </Label>
        <Textarea
          id={`notes-${message.id}`}
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          placeholder="Reply status, quote owner, next steps..."
          className="mt-1"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2 w-full sm:w-auto"
          disabled={saving || noteDraft.trim().length === 0}
          onClick={async () => {
            await onSaveNotes(message, noteDraft);
            setNoteDraft("");
          }}
        >
          Save notes
        </Button>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-brand-900">Note history</h3>
          {message.notes.length > 0 && (
            <span className="text-xs text-brand-400">
              {message.notes.length} {message.notes.length === 1 ? "note" : "notes"}
            </span>
          )}
        </div>
        {message.notes.length === 0 ? (
          <p className="mt-2 rounded-lg border border-dashed border-brand-200 p-3 text-sm text-brand-500">
            No internal notes yet.
          </p>
        ) : (
          <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-brand-100 bg-brand-50 p-2">
            <div className="grid gap-2">
              {message.notes.map((note) => (
                <article key={note.id} className="rounded-md border border-brand-100 bg-white p-3 shadow-sm">
                  <p className="whitespace-pre-wrap text-sm text-brand-800">{note.body}</p>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="min-w-0 break-words text-xs text-brand-400">By {note.authorName}</span>
                    {note.canDelete && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-red-600 hover:bg-red-50"
                        disabled={saving}
                        onClick={() => onDeleteNote(message, note)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </Button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 grid gap-2 sm:grid-cols-2">
        <Button
          type="button"
          variant="outline"
          disabled={saving}
          onClick={() => onStatus(message.id, "in_review")}
        >
          Mark in review
        </Button>
        <Button
          type="button"
          variant="accent"
          disabled={saving}
          onClick={() => onStatus(message.id, "closed")}
        >
          <CheckCircle2 className="h-4 w-4" />
          Close
        </Button>
      </div>
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-dashed border-brand-200 bg-white p-6 text-center">
      <p className="font-medium text-brand-900">{title}</p>
      <p className="mt-1 text-sm text-brand-500">{body}</p>
    </div>
  );
}
